import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Fade,
  TableFooter,
} from "@mui/material";
import { find, includes, map, mergeRight, propEq } from "ramda";
import { isBlank, notBlank } from "../../../util/ext";
import makeStyles from "@mui/styles/makeStyles";
import {
  useAllModules,
  useSubscriptions,
} from "../../../manage-subscriptions/subscriptionService";
import { useModuleUpdate } from "../moduleServices";
import { useOrganizations } from "../../../user-admin/userServices";
import { useSnackAlert } from "../../../common/use-snackAlert";
import { useSubscriptionHandler } from "../../../manage-subscriptions/use-SubscriptionHandler";
import { useSearchFilter } from "../../../common/use-searchFilter";
import { useNavigate, useLocation } from "react-router-dom";
import { DateTime } from "luxon";
import { useConfirm } from "../../../common/use-confirm";
import PropTypes from "prop-types";
import { faPlus, faTrashCan } from "@fortawesome/pro-regular-svg-icons";

import Button from "@mui/material/Button";
import EditIcon from "@mui/icons-material/Edit";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import ConfirmDialog from "../../../components/ConfirmDialog";
import ConfirmButton from "../../../components/ConfirmButton";
import FontAwesomeIcon from "../../../components/FontAwesomeIcon";
import MediaStatusSelect from "../../common/MediaStatusSelect";
import PanelLoadingIndicator from "../../../components/PanelLoadingIndicator";
import SubscriptionDialog from "../../../manage-subscriptions/SubscriptionDialog";

const useStyles = makeStyles(theme => ({
  action: {
    margin: 0,
  },

  active: {
    color: theme.palette.approved,
    fontWeight: 700,
  },

  header: {
    paddingTop: 10,
    paddingBottom: theme.spacing(1),
    height: 60,
    alignItems: "flex-start",
  },

  btnRoot: {
    lineHeight: 1.43,
    flexDirection: "column",
    alignItems: "flex-start",
  },

  cardHeaderContent: {
    flex: 2,
  },

  content: {
    paddingTop: 0,
  },

  cell: {
    minWidth: 115,
  },

  formControl: {
    bottom: "-5px",
  },

  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
}));

const getConfirmation = status => {
  switch (status) {
    case "INACTIVE":
      return {
        title: "Deactivate Module?",
        body:
          "Deactivating a Module will remove all subscriptions and remove the Module from BiblioBoard.",
        confirmButtonText: "Deactivate",
      };
    case "DELETED":
      return {
        title: "Delete Module?",
        body:
          "Deleting a Module will remove all subscriptions and remove the Module from BiblioBoard.",
        confirmButtonText: "Delete",
      };
    default:
      return { title: "", body: "", confirmButtonText: "" };
  }
};

const initialState = {
  appliedOrganization: null,
  appliedModule: null,
  orderBy: "subscription.endDate",
  orderDirection: "asc",
  activeOnly: true,
  perpetualOnly: false,
  includeIndirect: true,
  appliedTypes: null,
  expiresBefore: null,
  page: 0,
};

const ModuleSubscriptionsCard = ({ module }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const confirmHandler = useConfirm();
  const { showConfirm } = confirmHandler;
  const [subscriptions, setSubscriptions] = useState([]);
  const [modules, setModules] = useState([]);
  const { SnackAlert, showAlert } = useSnackAlert();
  const [saveAlert, setSaveAlert] = useState(false);
  const timerAutoHide = React.useRef();
  const setAutoHideTimer = useCallback(timeoutDuration => {
    clearTimeout(timerAutoHide.current);
    timerAutoHide.current = setTimeout(() => {
      setSaveAlert(false);
    }, timeoutDuration);
  }, []);

  React.useEffect(() => {
    if (saveAlert) {
      setAutoHideTimer(3000);
    }

    return () => {
      clearTimeout(timerAutoHide.current);
    };
  }, [saveAlert, setAutoHideTimer]);

  const [searchState, setSearchState] = useState(
    mergeRight(initialState, { appliedModule: module.id }),
  );
  const { state, handleUpdateSort, handleUpdatePage } = useSearchFilter(
    searchState,
  );

  const modulesResponse = useAllModules();
  useEffect(() => {
    if (modulesResponse.isSuccess && notBlank(modulesResponse.data)) {
      setModules(modulesResponse.data);
    }
  }, [modulesResponse.isSuccess, modulesResponse.data]);

  const [organizations, setOrganizations] = useState([]);
  const allOrganizations = useOrganizations();
  useEffect(() => {
    if (allOrganizations.isSuccess && notBlank(allOrganizations.data)) {
      setOrganizations(allOrganizations.data);
    }
  }, [allOrganizations.isSuccess, allOrganizations.data]);

  const findOrganizationName = id => {
    const org = find(propEq("organizationId", id), organizations);
    return notBlank(org) ? org.organizationName : "...";
  };

  const subResponse = useSubscriptions(
    searchState.appliedOrganization,
    searchState.appliedModule,
    searchState.includeIndirect,
    searchState.activeOnly,
    searchState.perpetualOnly,
    searchState.appliedTypes,
    searchState.expiresBefore,
    notBlank(searchState.orderBy)
      ? `${searchState.orderBy} ${searchState.orderDirection}`
      : null,
    searchState.page,
    "all",
  );
  useEffect(() => {
    if (subResponse.isSuccess) {
      setSubscriptions(subResponse.data);
    }
  }, [subResponse.isSuccess, subResponse.data]);

  useEffect(() => {
    setSearchState(state);
  }, [state]);

  const handleRequestSort = useCallback(
    property => {
      const prop =
        property === state.orderBy && state.orderDirection === "desc"
          ? null
          : property;

      const direction =
        state.orderBy !== prop
          ? "asc"
          : state.orderDirection === "desc"
          ? "asc"
          : "desc";

      handleUpdateSort({
        property: prop,
        order: direction,
      });
    },
    [state, handleUpdateSort],
  );

  const updateModule = useModuleUpdate();

  const afterSubscriptionSave = useCallback(
    async subs => {
      try {
        // await updateModule.mutateAsync({
        //   data: {
        //     status: "ACTIVE",
        //   },
        //   resourceIds: {
        //     id: module.id,
        //   },
        // });
        setSaveAlert(true);
      } catch (err) {
        showAlert("error", [err.message]);
      }
    },
    [/* updateModule, module.id,*/ showAlert],
  );

  const {
    dialogOpen,
    currentSubscription,
    handleSubscriptionEditClick,
    handleNewSubscriptionClicked,
    handleSubscriptionDialogClose,
    handleSubscriptionDialogSave,
    handleSubscriptionDeleteClick,
    loading,
  } = useSubscriptionHandler({
    callbacks: {
      afterSave: afterSubscriptionSave,
    },
  });

  const handleDeleteAllSubscriptions = useCallback(async () => {
    try {
      await Promise.all(map(handleSubscriptionDeleteClick, subscriptions));
      setSaveAlert(true);
    } catch (err) {
      showAlert("error", [err.message]);
    }
  }, [showAlert, handleSubscriptionDeleteClick, subscriptions]);

  const updateModuleStatus = useCallback(
    async status => {
      try {
        await updateModule.mutateAsync({
          resourceIds: { id: module.id },
          data: {
            status: status,
          },
        });
      } catch (err) {
        showAlert("error", [err.message]);
      }
    },
    [module.id, showAlert, updateModule],
  );

  const deleteModule = useCallback(async () => {
    const confirmation = getConfirmation("DELETED");
    showConfirm(
      confirmation.title,
      confirmation.body,
      {
        onConfirm: async () => {
          try {
            if (notBlank(subscriptions)) {
              await Promise.all(
                map(handleSubscriptionDeleteClick, subscriptions),
              );
            }

            await updateModule.mutateAsync({
              resourceIds: { id: module.id },
              data: {
                status: "DELETED",
              },
            });
            setSaveAlert(true);
          } catch (err) {
            showAlert("error", [err.message]);
          }
        },
      },
      confirmation.confirmButtonText,
    );
  }, [
    handleSubscriptionDeleteClick,
    module.id,
    showAlert,
    showConfirm,
    subscriptions,
    updateModule,
  ]);

  const handleStatusChange = event => {
    const newStatus = event.target.value;
    switch (newStatus) {
      case "ACTIVE":
      case "INACTIVE":
        updateModuleStatus(newStatus);
        break;
      case "DELETED":
        deleteModule();
        break;
      default:
        return;
    }
  };

  return (
    <Card raised={false} style={{ height: "100%", marginTop: 15 }}>
      <SnackAlert />
      <ConfirmDialog handler={confirmHandler} />
      <CardHeader
        component={Grid}
        container
        classes={{
          root: classes.header,
          action: classes.action,
          content: classes.cardHeaderContent,
        }}
        disableTypography
        title={
          <Box fontSize="2rem" lineHeight="2.6rem" fontWeight="bolder">
            Status & Subscriptions
          </Box>
        }
        action={
          <Grid container justifyContent="flex-end">
            <Fade in={saveAlert}>
              <Box pr={5} color="green">
                Update Successful
              </Box>
            </Fade>

            <Grid item style={{ width: 150 }}>
              <MediaStatusSelect
                mediaType="module"
                value={module.status}
                onChange={handleStatusChange}
                FormControlProps={{ classes: { root: classes.formControl } }}
              />
            </Grid>

            <Tooltip title="Add Subscription">
              <span>
                <IconButton
                  disabled={includes(module.status, ["DELETED"])}
                  onClick={handleNewSubscriptionClicked}
                  size="large"
                >
                  <FontAwesomeIcon icon={faPlus} />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Delete All Subscriptions">
              <span>
                <IconButton
                  disabled={
                    module.status === "DELETED" || isBlank(subscriptions)
                  }
                  onClick={() =>
                    showConfirm(
                      "Delete all subscriptions?",
                      "Would you like to delete all subscriptions?",
                      { onConfirm: handleDeleteAllSubscriptions },
                      "Delete",
                    )
                  }
                  size="large"
                >
                  <FontAwesomeIcon icon={faTrashCan} />
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
        }
      />
      <CardContent classes={{ root: classes.content }}>
        {subResponse.isError && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            <Typography color="error">
              Error loading Module Subscriptions.
            </Typography>
          </Box>
        )}
        {(subResponse.isLoading || updateModule.isPending) && (
          <PanelLoadingIndicator />
        )}

        {includes(module.status, ["ACTIVE", "INACTIVE"]) &&
          isBlank(subscriptions) && (
            <Box padding={5} fontSize="1.4rem" fontStyle="italic">
              This module does not currently have any subscriptions.
            </Box>
          )}

        {(includes(module.status, ["DELETED"]) || isBlank(module.status)) && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            Change Module to ACTIVE or INACTIVE in order to create new
            subscriptions.
          </Box>
        )}

        {subResponse.isSuccess &&
          includes(module.status, ["ACTIVE", "INACTIVE"]) && (
            <TableContainer style={{ padding: 0 }}>
              <Table stickyHeader aria-label="Expiring Subscriptions">
                <TableHead>
                  <TableRow>
                    <TableCell key="organization" align="left">
                      Organization
                    </TableCell>

                    <TableCell key="type" align="left" className={classes.cell}>
                      <TableSortLabel
                        active={state.orderBy === "subscription.type"}
                        direction={state.orderDirection}
                        onClick={() => handleRequestSort("subscription.type")}
                      >
                        Type
                        {state.orderBy === "subscription.type" ? (
                          <span className={classes.visuallyHidden}>
                            {state.orderDirection === "desc"
                              ? "sorted descending"
                              : "sorted ascending"}
                          </span>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>

                    <TableCell
                      key="start-date"
                      align="left"
                      className={classes.cell}
                    >
                      <TableSortLabel
                        active={state.orderBy === "subscription.startDate"}
                        direction={state.orderDirection}
                        onClick={() =>
                          handleRequestSort("subscription.startDate")
                        }
                      >
                        Start Date
                        {state.orderBy === "subscription.startDate" ? (
                          <span className={classes.visuallyHidden}>
                            {state.orderDirection === "desc"
                              ? "sorted descending"
                              : "sorted ascending"}
                          </span>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>

                    <TableCell
                      key="end-date"
                      align="left"
                      className={classes.cell}
                    >
                      <TableSortLabel
                        active={state.orderBy === "subscription.endDate"}
                        direction={state.orderDirection}
                        onClick={() =>
                          handleRequestSort("subscription.endDate")
                        }
                      >
                        End Date
                        {state.orderBy === "subscription.endDate" ? (
                          <span className={classes.visuallyHidden}>
                            {state.orderDirection === "desc"
                              ? "sorted descending"
                              : "sorted ascending"}
                          </span>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>

                    <TableCell
                      key="actions"
                      align="center"
                      style={{ width: 100 }}
                    />
                  </TableRow>
                </TableHead>

                <TableBody>
                  {subscriptions
                    .slice(state.page * 4, state.page * 4 + 4)
                    .map(sub => {
                      return (
                        <TableRow hover tabIndex={-1} key={sub.id}>
                          <TableCell
                            key={"organization-name:" + sub.id}
                            align="left"
                            className={classes.cell}
                          >
                            <Button
                              onClick={() =>
                                navigate(
                                  `/manage-organizations/organizations/${sub.organizationId}`,
                                  {
                                    state: {
                                      label: "Back to " + module.name,
                                      previousLocation:
                                        "/creator/modules/" + module.id,
                                    },
                                  },
                                )
                              }
                              aria-label={`Go to ${findOrganizationName(
                                sub.organizationId,
                              )}`}
                              classes={{
                                root: classes.btnRoot,
                              }}
                            >
                              <Box>
                                {findOrganizationName(sub.organizationId)}
                              </Box>
                              <Box fontSize="1.2rem">
                                ({sub.organizationId})
                              </Box>
                            </Button>
                          </TableCell>
                          <TableCell
                            key={"type:" + sub.id}
                            align="left"
                            className={classes.cell}
                          >
                            {sub.type}
                          </TableCell>
                          <TableCell
                            key={"start-date:" + sub.id}
                            align="left"
                            className={classes.cell}
                          >
                            {DateTime.fromISO(sub.startDate)
                              .toUTC()
                              .toLocaleString()}
                          </TableCell>
                          <TableCell
                            key={"end-date:" + sub.id}
                            align="left"
                            className={classes.cell}
                          >
                            {DateTime.fromISO(sub.endDate)
                              .toUTC()
                              .toLocaleString()}
                          </TableCell>
                          <TableCell
                            key={"actions:" + sub.id}
                            align="center"
                            style={{ width: 100 }}
                          >
                            <Box
                              display="flex"
                              justifyContent="flex-end"
                              alignItems="center"
                              pl={4}
                            >
                              <Tooltip title="Edit">
                                <IconButton
                                  classes={{ label: classes.icon }}
                                  onClick={() =>
                                    handleSubscriptionEditClick(sub)
                                  }
                                  size="large"
                                >
                                  <EditIcon color="primary" fontSize="large" />
                                </IconButton>
                              </Tooltip>

                              <ConfirmButton
                                loading={includes(sub.id, loading)}
                                dialogTitle="Delete Subscription"
                                dialogBody="Are you sure you want to delete this subscription?"
                                handleDelete={() =>
                                  handleSubscriptionDeleteClick(sub)
                                }
                              />
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell>
                      <Button
                        variant="text"
                        onClick={() =>
                          navigate(`/manage-subscriptions`, {
                            state: {
                              module,
                              previousLocation: location.pathname,
                            },
                          })
                        }
                      >
                        All Subscriptions
                      </Button>
                    </TableCell>
                    <TablePagination
                      classes={{
                        root: classes.pagination,
                      }}
                      rowsPerPageOptions={[4]}
                      colSpan={5}
                      count={subscriptions.length}
                      rowsPerPage={4}
                      page={state.page}
                      onPageChange={(event, page) => handleUpdatePage(page)}
                      nextIconButtonProps={{
                        disabled:
                          subscriptions.slice(
                            state.page * 4,
                            state.page * 4 + 4,
                          ).length < 4,
                      }}
                      labelDisplayedRows={({ page }) => `Page ${page + 1}`}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          )}
      </CardContent>
      {dialogOpen && (
        <SubscriptionDialog
          open={dialogOpen}
          subscription={currentSubscription}
          organizationList={organizations}
          moduleList={modules}
          preselectedModules={[module]}
          onClose={handleSubscriptionDialogClose}
          onSave={handleSubscriptionDialogSave}
        />
      )}
    </Card>
  );
};

ModuleSubscriptionsCard.propTypes = {
  id: PropTypes.string.isRequired,
};

export default ModuleSubscriptionsCard;
